import React, { useEffect, useState } from 'react';
import { isCaptureVisit } from '../../themeHost/captureScreen';
import type { ElementNodeDto } from '@xshop/shared';
import { CheckoutModal } from '../../components/CheckoutModal';
import { readMemory, writeMemory } from '../../tracking/visitorMemory';
import { useElementBox, useElementRender, type ElementBoxAttrs, type ElementComponent } from './elementRenderContext';
import { boolProp, numberProp, textProp } from './elementProps';
import './elementPopup.css';

// Sleek's newsletter popup: a box of ordinary elements that opens as a dialog a few seconds after a shopper
// arrives, at most once every few days for the same shopper. Inside the builder it is drawn in place, so
// the merchant designs it like any other box; it is off until they switch it on.

/** How long a day is, for counting how recently a shopper closed the popup. */
const DAY_MS = 86_400_000;

// Reports whether this shopper closed this popup recently enough that it should stay shut.
const closedRecently = (id: string, days: number): boolean => {
  const at = Number(readMemory(`popup:${id}`, 'visitor'));
  return Number.isFinite(at) && at > 0 && Date.now() - at < days * DAY_MS;
};

/** What the shopper's dialog draws: the popup's node, its styled box, and its rendered children. */
type ShopperPopupProps = { node: ElementNodeDto; box: ElementBoxAttrs; children: React.ReactNode };

// Opens the popup after its delay unless this shopper closed it lately, and remembers when they close it; a picture of
// the store taken for the themes screen shows the store, never a popup over it.
const ShopperPopup: React.FC<ShopperPopupProps> = ({ node, box, children }) => {
  const [open, setOpen] = useState(false);
  const delay = Math.max(0, numberProp(node.props, 'delay', 5));
  const days = Math.max(1, numberProp(node.props, 'againAfter', 7));
  useEffect(() => {
    if (closedRecently(node.id, days) || isCaptureVisit()) return;
    const timer = setTimeout(() => setOpen(true), delay * 1000);
    return () => clearTimeout(timer);
  }, [node.id, delay, days]);
  const close = () => { writeMemory(`popup:${node.id}`, 'visitor', String(Date.now())); setOpen(false); };
  return (
    <CheckoutModal open={open} placement="popup" title={textProp(node.props, 'label', 'Newsletter')} closeLabel="Close" onClose={close}>
      <div {...box} className={[box.className, 'xe-popup'].filter(Boolean).join(' ')}>{children}</div>
    </CheckoutModal>
  );
};

// Reports whether this page draws one section-library block on its own, as its card's picture is taken from.
const blockPicture = (): boolean => new URLSearchParams(window.location.search).has('blockPreview');

// Draws the popup: in place inside the builder and a block's picture, as a dialog for a shopper once on, else nothing.
export const PopupElement: ElementComponent = ({ node, children }) => {
  const box = useElementBox(node);
  const { editing } = useElementRender();
  const enabled = boolProp(node.props, 'enabled');
  if (editing || blockPicture()) {
    return (
      <div className="xe-popup-design">
        <p className="xe-popup-note">{enabled ? `Popup — opens ${numberProp(node.props, 'delay', 5)} seconds after a shopper arrives.` : 'Popup — switched off. Turn it on in its settings to show it to shoppers.'}</p>
        <div {...box} className={[box.className, 'xe-popup'].filter(Boolean).join(' ')}>{children}</div>
      </div>
    );
  }
  return enabled ? <ShopperPopup node={node} box={box}>{children}</ShopperPopup> : null;
};
